<template>
  <v-form ref="registerForm" v-model="valid">
    <LoadingBar :hide="!loading" :error="error" />
    <v-card v-show="!loading" class="px-4">
      <v-stepper v-model="currentStep" vertical>
        <v-stepper-step step="1" :complete="currentStep >= 1">
          Account Credentials
        </v-stepper-step>

        <v-stepper-content step="1">
          <v-card color="" class="mb-5" style="box-shadow: none;">
            <v-row>
              <v-col cols="12" class="pb-0">
                <v-text-field
                  label="Username"
                  ref="usernameField"
                  v-model="registerUsername"
                  :rules="rules.username"
                  id="username"
                  :error-messages="usernameErrors"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-text-field
                  label="Email"
                  id="email"
                  v-model="registerEmail"
                  :rules="rules.email"
                  autocomplete="new-email"
                  name="new-email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <v-text-field
                  id="password"
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="registerPassword"
                  :rules="rules.password"
                  @click:append="showPassword = !showPassword"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  name="new-password"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  id="password-confirm"
                  ref="passwordConfirm"
                  label="Password (confirm)"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  v-model="password_confirm"
                  v-on:keyup.enter="currentStep = !valid ? currentStep : 2"
                  :rules="rules.password_confirm"
                  @click:append="showConfirmPassword = !showConfirmPassword"
                  :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>
          <v-btn
            :disabled="!valid"
            color="primary"
            @click="currentStep = 2"
            id="continue-1"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step step="2" :complete="currentStep >= 2">
          Lab
        </v-stepper-step>

        <v-stepper-content step="2">
          <v-card color="" class="mb-5" style="box-shadow: none;">
            <v-row>
              <v-col cols="12" class="pb-0">
                <v-autocomplete
                  :items="labs"
                  label="Select a Lab"
                  no-data-text="No matches were found"
                  item-text="title"
                  item-value="id"
                  v-model="lab_id"
                  id="lab-id"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card>
          <v-btn
            @click="currentStep = 1"
            id="prev-1"
            style="margin-right: 1rem;"
          >
            Previous
          </v-btn>
          <v-btn
            id="continue-2"
            :disabled="!valid"
            color="primary"
            @click="currentStep = 3"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step step="3" :complete="currentStep >= 3">
          Account Details
        </v-stepper-step>

        <v-stepper-content step="3">
          <v-card
            v-if="currentStep === 3"
            class="mb-5"
            style="box-shadow: none;"
          >
            <v-row>
              <v-col cols="12">
                <v-text-field
                  id="business-name"
                  label="Business Name"
                  v-model="business_name"
                  :rules="rules.name"
                  maxlength="255"
                  :error-messages="businessNameErrors"
                  v-on:keyup.enter="currentStep = !valid ? currentStep : 4"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="First Name"
                  id="first-name"
                  v-model="first_name"
                  maxlength="255"
                  :rules="rules.name"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="Last Name"
                  id="last-name"
                  v-model="last_name"
                  maxlength="255"
                  :rules="rules.name"
                  v-on:keyup.enter="currentStep = !valid ? currentStep : 4"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>
          <v-btn
            @click="currentStep = 2"
            id="prev-2"
            style="margin-right: 1rem;"
          >
            Previous
          </v-btn>
          <v-btn
            :disabled="!valid"
            color="primary"
            id="continue-3"
            @click="currentStep = 4"
          >
            Continue
          </v-btn>
        </v-stepper-content>

        <v-stepper-step step="4" :complete="currentStep >= 4">
          Billing Terms
        </v-stepper-step>

        <v-stepper-content step="4">
          <v-card
            v-if="currentStep === 4"
            class="mb-5"
            style="box-shadow: none;"
          >
            <v-row>
              <v-col cols="12" class="text-justify">
                <v-dialog
                  v-model="billing_terms_dialog"
                  scrollable
                  max-width="580px"
                  min-width="320px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      id="view-billing-terms"
                      dark
                      v-bind="attrs"
                      v-on="on"
                    >
                      View Billing Terms
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title
                      >The billing information and terms are listed
                      below</v-card-title
                    >
                    <v-divider></v-divider>
                    <v-card-text style="height: 340px;" class="pt-4">
                      <p>
                        Selecting this agreement is required to complete the
                        online client account.
                      </p>
                      <p>
                        Please note that our billing terms are net 30.
                      </p>
                      <p>
                        We charge an annual late charge of 18% on any balance 30
                        days past due.
                      </p>
                      <p>
                        Bills are generated around the 1st and 15th of each
                        month.
                      </p>
                      <p>
                        PLEASE PAY FROM THE INVOICE. Statements are generated on
                        the 28th of each month ONLY if there is a past due
                        balance. If you have billing questions or concerns
                        please contact us at accounting@foragelab.com. If my
                        account is not paid in a timely manner and it becomes
                        necessary to refer my account to a Collection Agency, I
                        understand that I will be responsible for all collection
                        agency fees to include reasonable attorney fees and
                        court costs.
                      </p>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-btn
                        color="primary"
                        text
                        @click="
                          agree_terms = true;
                          billing_terms_dialog = false;
                        "
                        id="accept-billing-terms"
                      >
                        Accept Terms
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-checkbox
                  label="I agree to the terms of payment as a client."
                  v-model="agree_terms"
                  :rules="rules.agree_terms"
                  id="accept-billing-terms-checkbox"
                  class="ml-3"
                />
              </v-col>
            </v-row>
            <v-btn
              @click="currentStep = 3"
              id="prev-3"
              style="margin-right: 1rem;"
            >
              Previous
            </v-btn>
          </v-card>
        </v-stepper-content>
      </v-stepper>

      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn
          x-large
          block
          :disabled="!canRegister"
          color="success"
          @click="submit"
          id="register-submit"
        >
          Register
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { mapFields } from "vuex-map-fields";
import LoadingBar from "@/components/loaders/LoadingBar";
import rules from "@/utils/rules";

/**
 * @property {string} username
 * @property {string} password
 * @property {string} business_name
 * @property {string} first_name
 * @property {string} last_name
 * @property {boolean} agree_terms
 * @property {boolean|string} error
 */
export default {
  name: "Register",
  props: ["clear-form"],
  components: { LoadingBar },
  data() {
    return {
      loading: false,
      currentStep: 1,
      valid: false,
      password_confirm: "",
      showPassword: false,
      showConfirmPassword: false,
      agree_terms: false,
      billing_terms_dialog: false
    };
  },
  computed: {
    ...mapFields("auth", [
      "registerUsername",
      "registerEmail",
      "registerPassword",
      "is_register",
      "is_copy_to_register",
      "registration.first_name",
      "registration.last_name",
      "registration.business_name",
      "registration.error",
      "registration.lab_id",
      "is_unique_title_error",
      "is_unique_username_error",
      "activeTab"
    ]),
    ...mapGetters("labs", { labs: "getActiveItems" }),
    rules() {
      let componentRules = {
        required: [rules.required],
        email: [rules.required, rules.email],
        password: [rules.password],
        password_confirm: [
          rules.required,
          v => v === this.registerPassword || "Passwords do not match"
        ],
        username: [rules.required, rules.username],
        name: [rules.required, rules.alphabetic]
      };

      const businessOrName = field => {
        return () => {
          return (
            !!this.business_name ||
            !!field ||
            "Business Name or First/Last Name are required"
          );
        };
      };

      const businessOrNames = () => {
        return (
          !!this.business_name ||
          !!this.first_name ||
          !!this.last_name ||
          "Business Name or First/Last Name are required"
        );
      };

      const must_agree = () => {
        return (
          !!this.agree_terms || "You must agree to payment terms to continue."
        );
      };

      // Separating these into individual componentRules, that way current field with a value won't show validation errors
      componentRules.businessOrFirstName = [
        businessOrName(this.first_name),
        rules.alphabetic
      ];
      componentRules.businessOrLastName = [
        businessOrName(this.last_name),
        rules.alphabetic
      ];
      componentRules.businessOrNames = [businessOrNames, rules.alphabetic];
      componentRules.agree_terms = [must_agree];

      return componentRules;
    },
    businessGroupValid() {
      const hasValue =
        !!this.business_name && (!!this.first_name || !!this.last_name);
      const firstValid =
        !this.first_name || rules.alphabetic(this.first_name) === true;
      const lastValid =
        !this.last_name || rules.alphabetic(this.last_name) === true;

      return hasValue && firstValid && lastValid;
    },
    canRegister() {
      return this.currentStep === 4 && this.valid && this.loading !== true;
    },
    usernameErrors() {
      return this.is_unique_username_error ? ["Username must be unique"] : [];
    },
    businessNameErrors() {
      return this.is_unique_title_error ? ["Business name must be unique"] : [];
    }
  },
  watch: {
    registerPassword() {
      this.$refs.passwordConfirm && this.$refs.passwordConfirm.validate();
    },
    clearForm(val) {
      if (!val) {
        this.$refs.registerForm.resetValidation();
        this.password_confirm = "";
        this.currentStep = 1;
      }
    },
    registerUsername() {
      this.is_unique_title_error = false;
      this.is_unique_username_error = false;
    },
    business_name() {
      this.is_unique_title_error = false;
      this.is_unique_username_error = false;
    }
  },
  methods: {
    ...mapActions("auth", ["submitRegistration", "login"]),
    ...mapActions("labs", ["getActiveLabs"]),
    ...mapMutations("auth", ["clearRegistrationDetails"]),
    checkBusinessGroup() {
      this.valid = this.businessGroupValid;
    },
    submit() {
      if (this.validate()) {
        this.loading = true;
        this.submitRegistration()
          .then(res => {
            if (res) {
              this.is_register = true;
              this.is_copy_to_register = false;
              return this.login()
                .then(() => {
                  this.activeTab = 0;
                  this.loading = false;
                })
                .then(this.clearRegistrationDetails())
                .then((this.password_confirm = ""));
            } else {
              this.loading = false;

              if (this.is_unique_username_error) {
                this.currentStep = 1;
                this.$nextTick(() => {
                  this.$refs.usernameField?.focus();
                  this.$refs.usernameField?.validate();
                });
                return;
              }

              if (this.is_unique_title_error) {
                this.currentStep = 3;
                this.$nextTick(() => {
                  this.$refs.businessNameField?.focus();
                  this.$refs.businessNameField?.validate();
                });
                return;
              }

              this.currentStep = 1;
            }
          })
          .catch(e => {
            this.currentStep = 1;
            this.loading = false;
            console.error("error", e);
            this.$nextTick(() => {
              if (this.$refs.usernameField) {
                this.$refs.usernameField.focus();
              }
            });
          });
      }
    },
    validate() {
      return this.$refs.registerForm.validate();
    }
  },
  mounted() {
    Promise.all([this.getActiveLabs()]).then(data => {
      if (data.includes(false)) {
        console.error("An error occurred while retrieving the labs data");
        // this.error.message = "An error occurred while retrieving the labs data";
      }
      this.loading = false;
    });
    this.password_confirm = "";
  }
};
</script>

<style scoped></style>
